import { ClearCaseViewTableAction, executeCaseTableViewReport, deleteCaseById, copyRiskEvaluation }
  from 'app/area/case-report/case-table-view/actions';
import { useInstanceKey, useInstanceOptions } from 'app/area/favourite/favourite-context';
import { debounceExecuteFavouriteWait } from 'app/constants';
import { FavouriteDefinition } from 'app/types-shared';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { CaseTableActionType } from 'app/area/case-report/case-table-view/action-types';
import { debounce } from 'lodash';
import { selectCaseTableViewInstanceState } from 'app/area/case-report/case-table-view/selectors';
import { ApplicationState } from 'app/types-store';
import { AddRiskEvaluationInitialValues } from 'app/area/case/features/loss-potential-evaluation/types';

export function useCaseTableViewEvents(
  workingDefinition: FavouriteDefinition,
  autoExecute: boolean
): {
  instanceKey: string;
  execute: (startIndex?: number) => void;
  windowWidth: number;
  onDeleteCase: (caseId: number, key?: string) => void;
  onCopyRiskEvaluation?: (caseId: number, riskMatrixInstance: number, instanceCopyCmd: number, 
    initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean) => void;
} {
  const instanceKey = useInstanceKey();
  const dispatch: ThunkDispatch<any, any, CaseTableActionType> = useDispatch();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const {
    executionCounter, 
    // To override the API for the report in the dashboard
    reportApiOverrides 
  } = useInstanceOptions();

  const debouncedExecute = useCallback(
    debounce(async (definition: FavouriteDefinition, startIndex: number) => {
      dispatch(
        executeCaseTableViewReport({
          definition,
          instanceKey,
          startIndex,
          overrideExecuteFavourite: reportApiOverrides?.executeFavourite
        })
      );
    }, debounceExecuteFavouriteWait),
    [instanceKey, debounceExecuteFavouriteWait]
  );

  const execute = useCallback(
    (startIndex?: number) => {
      debouncedExecute(workingDefinition, startIndex);
    },
    [workingDefinition, debouncedExecute]
  );

  useEffect(() => {
    autoExecute && execute(0);
    return () => {
      dispatch(ClearCaseViewTableAction(instanceKey));
    };
  }, [autoExecute, instanceKey, workingDefinition, dispatch, executionCounter]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onDeleteCase = (caseId: number) => {
    dispatch(deleteCaseById(instanceKey, caseId));
  };

  const onCopyRiskEvaluation = (caseId: number, riskMatrixInstance: number, instanceCopyCmd: number, 
    initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean) => {
    dispatch(copyRiskEvaluation(instanceKey, caseId, riskMatrixInstance, instanceCopyCmd, initialValues, copyToResidual));
  };

  return {
    instanceKey,
    execute,
    windowWidth,
    onDeleteCase,
    onCopyRiskEvaluation
  };
}

export function useSelectors() {
  const instanceKey = useInstanceKey();
  const caseTableViewData = useSelector(selectCaseTableViewInstanceState(instanceKey));
  const locale = useSelector((state: ApplicationState) => state?.app?.locale);
  const { entities, metadata } = caseTableViewData?.caseTableViewResults || {};
  const errorsExecuteFailed = caseTableViewData?.errorsExecuteFailed;
  const access = useSelector((state: ApplicationState) => state?.app?.userInfo?.access);  
  return {
    fetching: !!caseTableViewData?.fetching,
    columns: metadata?.includedFields,
    entities,
    totalNumberOfResults: metadata?.totalNumberOfResults,
    locale,
    errorsExecuteFailed,
    access
  };
}


